import type { OrderAmountSummaryQuery, OrderAmountSummaryResponse } from "../../../domain/types/company/company-statistics";
import type {
  ApiResponse,
  ApiResponseShopManagementDto,
  RegistrationStatsResponse,
  VisitorStatsResponse,
  OrderStatsResponse,
  PaymentStatsResponse,
} from "../../../shared/types/api";
import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";

export class CompanyStatisticsApiService {
  async getOrderAmountSummary(companyId: number, query: OrderAmountSummaryQuery): Promise<ApiResponse<OrderAmountSummaryResponse>> {
    const params = Object.fromEntries(Object.entries(query).filter(([, value]) => value));
    const response = await httpClient.get<ApiResponse<OrderAmountSummaryResponse>>(
      `/api/company/${companyId}/statistics/order-amounts`, { params },
    );
    if (response.data.errorCode) throw new Error(response.data.errorMsg || "주문금액 통계를 불러오지 못했습니다.");
    return response.data;
  }
  async getRegistrationStats(
    companyId: number,
    period: string,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<RegistrationStatsResponse>> {
    const response = await httpClient.get<
      ApiResponse<RegistrationStatsResponse>
    >(
      NEW_API_ENDPOINTS.companyStatisticsController.getRegistrationStats(
        companyId,
      ),
      {
        params: {
          period,
          startDate,
          endDate,
        },
      },
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "가입자수 통계를 불러오지 못했습니다.",
      );
    }

    return response.data;
  }

  async getVisitorStats(
    companyId: number,
    period: string,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<VisitorStatsResponse>> {
    const response = await httpClient.get<ApiResponse<VisitorStatsResponse>>(
      NEW_API_ENDPOINTS.companyStatisticsController.getVisitorStats(companyId),
      {
        params: {
          period,
          startDate,
          endDate,
        },
      },
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "방문자수 통계를 불러오지 못했습니다.",
      );
    }

    return response.data;
  }

  async getOrderStats(
    companyId: number,
    period: string,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<OrderStatsResponse>> {
    const response = await httpClient.get<ApiResponse<OrderStatsResponse>>(
      NEW_API_ENDPOINTS.companyStatisticsController.getOrderStats(companyId),
      {
        params: {
          period,
          startDate,
          endDate,
        },
      },
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "주문량 통계를 불러오지 못했습니다.",
      );
    }

    return response.data;
  }

  async getPaymentStats(
    companyId: number,
    period: string,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<PaymentStatsResponse>> {
    const response = await httpClient.get<ApiResponse<PaymentStatsResponse>>(
      NEW_API_ENDPOINTS.companyStatisticsController.getPaymentStats(companyId),
      {
        params: {
          period,
          startDate,
          endDate,
        },
      },
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "결제금액 통계를 불러오지 못했습니다.",
      );
    }

    return response.data;
  }
}

export const companyStatisticsApiService = new CompanyStatisticsApiService();
